import { type ReactNode, type Ref, useState } from 'react';

import { Field, FieldDescription, FieldError, FieldLabel } from './field';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from './input-group';

type Props = {
    ref?: Ref<HTMLInputElement>;
    id: string;
    label?: string;
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    error?: string;
    disabled?: boolean;
    autoComplete: 'new-password' | 'current-password';
    description?: ReactNode;
    labelAction?: ReactNode;
};

export function AuthPasswordField({
    ref,
    id,
    label = 'Password',
    value,
    onChange,
    onBlur,
    error,
    disabled,
    autoComplete,
    description,
    labelAction,
}: Props) {
    const [visible, setVisible] = useState(false);
    const describedById = error ? `${id}-error` : description ? `${id}-description` : undefined;

    return (
        <Field data-invalid={error ? true : undefined}>
            <div className="flex items-center justify-between gap-2">
                <FieldLabel htmlFor={id}>{label}</FieldLabel>
                {labelAction}
            </div>
            <InputGroup>
                <InputGroupInput
                    ref={ref}
                    id={id}
                    type={visible ? 'text' : 'password'}
                    autoComplete={autoComplete}
                    value={value}
                    disabled={disabled}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={describedById}
                    onChange={(event) => onChange(event.target.value)}
                    onBlur={onBlur}
                />
                <InputGroupAddon align="inline-end">
                    <InputGroupButton
                        variant="link"
                        aria-pressed={visible}
                        onClick={() => setVisible((prev) => !prev)}
                    >
                        {visible ? 'Hide' : 'Show'}
                    </InputGroupButton>
                </InputGroupAddon>
            </InputGroup>
            {error ? (
                <FieldError id={`${id}-error`}>{error}</FieldError>
            ) : description ? (
                <FieldDescription id={`${id}-description`}>{description}</FieldDescription>
            ) : null}
        </Field>
    );
}
